/**
 * FailedLoginActivityTile — super-admin dashboard tile surfacing
 * recent failed-login activity. Pairs with the failed-login spike
 * Slack alert in services/failed_login_alert.py: the alert tells
 * security WHEN to look; this tile shows them WHAT to look at.
 *
 * Each row: rank · IP · count · distinct emails · 24h sparkline.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Shield, Activity, Users, AlertTriangle, X, Plus, Check, Ban, Clock, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useTelemetryTile } from '../../hooks';

// localStorage key for the collapsed/expanded state. Keyed by feature
// name (not user) — the preference is per-device, not per-account.
const COLLAPSE_STORAGE_KEY = 'admin.failedLoginTile.expanded';

const WINDOW_OPTIONS = [
  { label: '1h', hours: 1 },
  { label: '24h', hours: 24 },
  { label: '7d', hours: 168 },
];

/** ISO-3166 alpha-2 country code → regional indicator emoji. The
 *  flag emoji is built by mapping each ASCII letter to its
 *  REGIONAL INDICATOR codepoint (U+1F1E6 .. U+1F1FF). Browsers/OSes
 *  that don't render flags will show the two letters anyway. */
function flagEmoji(code) {
  if (!code || code.length !== 2) return null;
  const upper = code.toUpperCase();
  const A = 0x1f1e6;
  return String.fromCodePoint(A + (upper.charCodeAt(0) - 65), A + (upper.charCodeAt(1) - 65));
}

/** Tiny inline SVG sparkline. Fixed [0, max] vertical scale per
 * row so a quiet hour doesn't squash a busy hour. */
function HourlySparkline({ buckets, ip }) {
  if (!buckets || buckets.length === 0) return null;
  const W = 80, H = 18, PAD = 1;
  const max = Math.max(1, ...buckets);
  const xAt = (i) => PAD + (i * (W - 2 * PAD)) / Math.max(1, buckets.length - 1);
  const yAt = (v) => PAD + ((max - v) * (H - 2 * PAD)) / max;
  const points = buckets.map((v, i) => `${xAt(i)},${yAt(v)}`).join(' ');
  return (
    <svg
      width={W}
      height={H}
      className="inline-block align-middle"
      aria-label={`${ip} failed-login activity`}
      data-testid={`failed-login-sparkline-${ip}`}
    >
      <polyline
        fill="none"
        stroke="#dc2626"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />
      {buckets.map((v, i) => (
        v > 0 ? (
          <circle key={i} cx={xAt(i)} cy={yAt(v)} r="1.25" fill="#dc2626">
            <title>{`Hour ${i + 1}: ${v} failure${v === 1 ? '' : 's'}`}</title>
          </circle>
        ) : null
      ))}
    </svg>
  );
}

export default function FailedLoginActivityTile() {
  const [windowHours, setWindowHours] = useState(24);
  const [editingAllowlist, setEditingAllowlist] = useState(false);
  const [allowlistInput, setAllowlistInput] = useState('');
  const [allowlistSaving, setAllowlistSaving] = useState(false);

  // Collapsed/expanded state — defaults to collapsed so the tile
  // sits at the same compact height as the Pending Invitations
  // tile next to it. Choice persists in localStorage so admins who
  // prefer the always-on view don't have to re-expand on every
  // page load. Auto-expands once when an active incident lands
  // (autoBlocks.total_24h > 0) so a security event isn't hidden
  // behind a click.
  const [expanded, setExpanded] = useState(() => {
    try {
      const saved = localStorage.getItem(COLLAPSE_STORAGE_KEY);
      return saved === null ? false : saved === 'true';
    } catch {
      return false;
    }
  });
  const [autoExpandedOnce, setAutoExpandedOnce] = useState(false);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_STORAGE_KEY, String(next));
      } catch { /* localStorage unavailable — preference simply doesn't persist */ }
      return next;
    });
  }, []);
  // Auto-block roll-up — pulled from /security/auto-blocks. Tile
  // re-fetches when the failed-login window changes (cheap query
  // and the user has clearly engaged with the security strip).
  const [autoBlocks, setAutoBlocks] = useState({ rows: [], total_24h: 0 });
  // Active temp-allow overrides — set Map IP → expires_at so the
  // strip can swap "Allow 24h" → "✓ Allowed (revoke)" for IPs the
  // super-admin has already whitelisted.
  const [tempAllowed, setTempAllowed] = useState({});
  const [pendingIp, setPendingIp] = useState(null);

  const { data, loading, error, refetch } = useTelemetryTile({
    url: `/security/failed-login-summary?window_hours=${windowHours}`,
    deps: [windowHours],
  });

  const refreshAutoBlocks = useCallback(async () => {
    try {
      const [blocks, allowed] = await Promise.all([
        api.get('/security/auto-blocks?limit=10'),
        api.get('/security/temp-allow-ip'),
      ]);
      setAutoBlocks({
        rows: blocks?.data?.rows || [],
        total_24h: blocks?.data?.total_24h || 0,
      });
      const map = {};
      (allowed?.data?.rows || []).forEach((r) => { map[r.ip] = r.expires_at; });
      setTempAllowed(map);
    } catch {
      /* tile gracefully degrades — strip just doesn't render */
    }
  }, []);

  // Fetch the last 10 auto-blocks once on mount + whenever the
  // failed-login summary refreshes (gives Ops a near-realtime
  // view of policy firings without spinning up its own poller).
  useEffect(() => {
    refreshAutoBlocks();
  }, [data, refreshAutoBlocks]);

  // Auto-expand once when an incident lands so a fresh auto-block
  // surfaces without requiring the admin to click. We only do this
  // ONE time per mount — if the admin re-collapses, we respect that
  // and don't keep forcing the panel open on every refresh.
  useEffect(() => {
    if (!autoExpandedOnce && autoBlocks.total_24h > 0 && !expanded) {
      setExpanded(true);
      setAutoExpandedOnce(true);
    }
  }, [autoBlocks.total_24h, expanded, autoExpandedOnce]);

  const handleAllow = async (ip) => {
    setPendingIp(ip);
    try {
      await api.post('/security/temp-allow-ip', { ip, ttl_hours: 24 });
      toast.success(`Allowed ${ip} for 24h`);
      await refreshAutoBlocks();
    } catch {
      toast.error(`Couldn't whitelist ${ip}`);
    } finally {
      setPendingIp(null);
    }
  };

  const handleRevoke = async (ip) => {
    setPendingIp(ip);
    try {
      await api.delete(`/security/temp-allow-ip/${encodeURIComponent(ip)}`);
      toast.success(`Revoked override for ${ip}`);
      await refreshAutoBlocks();
    } catch {
      toast.error(`Couldn't revoke ${ip}`);
    } finally {
      setPendingIp(null);
    }
  };

  const allowlist = data?.country_allowlist || [];

  const saveAllowlist = async () => {
    setAllowlistSaving(true);
    const codes = allowlistInput
      .split(/[\s,]+/)
      .map((c) => c.trim().toUpperCase())
      .filter((c) => c.length === 2 && /^[A-Z]+$/.test(c));
    try {
      await api.put('/security/country-allowlist', {
        country_codes: codes,
        unexpected_threshold: 5,
      });
      setEditingAllowlist(false);
      await refetch();
    } catch {
      // Silent — error surfaces in the UI on next refetch via the
      // existing error state.
    } finally {
      setAllowlistSaving(false);
    }
  };

  const startEditAllowlist = () => {
    setAllowlistInput(allowlist.join(', '));
    setEditingAllowlist(true);
  };

  if (error === 'forbidden') return null;

  const top = (data?.top_ips || []).slice(0, 5);
  const totals = data?.totals || { events: 0, events_last_hour: 0, user_not_found: 0, wrong_password: 0 };
  const hasIncident = autoBlocks.total_24h > 0;

  return (
    <Card
      data-testid="failed-login-activity-tile"
      data-expanded={expanded ? 'true' : 'false'}
      className={
        'border-rose-200 bg-gradient-to-br from-white to-rose-50/40 '
        + (hasIncident ? 'ring-1 ring-rose-200' : '')
      }
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <button
            type="button"
            onClick={toggleExpanded}
            className="flex-1 min-w-0 flex items-start gap-2 text-left group"
            data-testid="failed-login-tile-toggle"
            aria-expanded={expanded}
            aria-controls="failed-login-tile-body"
          >
            <ChevronDown
              size={16}
              className={
                'mt-0.5 shrink-0 text-slate-500 transition-transform duration-200 '
                + (expanded ? 'rotate-0' : '-rotate-90')
              }
            />
            <div className="flex-1 min-w-0">
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="h-4 w-4 text-rose-600" />
                Failed-login Activity
                {hasIncident && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rose-700"
                    data-testid="failed-login-incident-badge"
                  >
                    <AlertTriangle size={10} />
                    {autoBlocks.total_24h} auto-blocked
                  </span>
                )}
              </CardTitle>
              {expanded && (
                <CardDescription className="mt-0.5">
                  Top offending IPs over the rolling window — the live view behind the failed-login spike Slack alert (fires at 20 fails/60m).
                </CardDescription>
              )}
              {/* Collapsed-state inline metrics — gives admins the
                  headline numbers at a glance without expanding. */}
              {!expanded && (
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px] text-slate-700" data-testid="failed-login-collapsed-metrics">
                  <span data-testid="metric-failures">
                    <span className="text-slate-500 mr-1">Failures</span>
                    <span className="font-semibold text-rose-700">{totals.events}</span>
                  </span>
                  <span data-testid="metric-last-hour">
                    <span className="text-slate-500 mr-1">Last hour</span>
                    <span className="font-semibold text-rose-700">{totals.events_last_hour}</span>
                  </span>
                  <span data-testid="metric-user-unknown">
                    <span className="text-slate-500 mr-1">User unknown</span>
                    <span className="font-semibold text-slate-800">{totals.user_not_found}</span>
                  </span>
                  <span data-testid="metric-wrong-password">
                    <span className="text-slate-500 mr-1">Wrong password</span>
                    <span className="font-semibold text-slate-800">{totals.wrong_password}</span>
                  </span>
                </div>
              )}
            </div>
          </button>
          {expanded && (
            <div className="inline-flex bg-white border border-slate-200 rounded-md p-0.5 shrink-0" data-testid="failed-login-window-selector">
              {WINDOW_OPTIONS.map((o) => (
                <button
                  key={o.hours}
                  type="button"
                  onClick={() => setWindowHours(o.hours)}
                  className={
                    'px-2 py-0.5 text-[11px] font-medium rounded transition-colors ' +
                    (windowHours === o.hours
                      ? 'bg-rose-600 text-white'
                      : 'text-slate-600 hover:bg-slate-100')
                  }
                  data-testid={`failed-login-window-${o.hours}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </CardHeader>
      {expanded && (
      <CardContent id="failed-login-tile-body" data-testid="failed-login-tile-body">
        {/* Auto-block roll-up — last 10 attempts the country
            allowlist policy short-circuited at the /auth/login
            edge. Renders only when the policy has actually fired
            in the past 24h so the tile stays clean for orgs that
            haven't tripped any blocks. */}
        {autoBlocks.total_24h > 0 && (
          <div
            className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5"
            data-testid="auto-blocks-strip"
          >
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-rose-700 font-bold">
                <Ban className="h-3 w-3" />
                Auto-blocked · last {Math.min(autoBlocks.rows.length, 10)} of {autoBlocks.total_24h} (24h)
              </span>
            </div>
            <ul
              className="space-y-0.5 max-h-32 overflow-y-auto pr-1"
              data-testid="auto-blocks-list"
            >
              {autoBlocks.rows.slice(0, 10).map((row, i) => {
                const isAllowed = !!tempAllowed[row.ip];
                const isPending = pendingIp === row.ip;
                return (
                  <li
                    key={`${row.ts}-${row.ip}-${i}`}
                    className="flex items-center gap-2 text-[11px]"
                    data-testid={`auto-block-row-${i}`}
                  >
                    <span className="text-base leading-none" title={row.country_name || row.country_code || 'unknown'}>
                      {flagEmoji(row.country_code) || '🏳'}
                    </span>
                    <span className="font-mono text-slate-700 truncate flex-1" title={row.ip}>
                      {row.ip || 'unknown'}
                    </span>
                    <span className="text-slate-500 truncate max-w-[140px]" title={row.email_masked || ''}>
                      {row.email_masked || '—'}
                    </span>
                    <span className="text-[10px] text-slate-400 tabular-nums w-12 text-right">
                      {row.ts ? new Date(row.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                    {row.ip && (
                      isAllowed ? (
                        <button
                          type="button"
                          onClick={() => handleRevoke(row.ip)}
                          disabled={isPending}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-800 hover:bg-emerald-200 disabled:opacity-50"
                          title={`Override expires at ${new Date(tempAllowed[row.ip]).toLocaleString()}`}
                          data-testid={`auto-block-revoke-${i}`}
                        >
                          <Check className="h-2.5 w-2.5" />
                          Allowed · revoke
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleAllow(row.ip)}
                          disabled={isPending}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                          data-testid={`auto-block-allow-${i}`}
                        >
                          <Clock className="h-2.5 w-2.5" />
                          Allow 24h
                        </button>
                      )
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Country allowlist editor */}
        <div
          className="mb-3 flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5"
          data-testid="country-allowlist-strip"
        >
          <span className="text-[10px] uppercase tracking-wider text-slate-500 flex-shrink-0">
            Expected countries
          </span>
          {!editingAllowlist ? (
            <>
              <div className="flex-1 flex flex-wrap gap-1">
                {allowlist.length === 0 ? (
                  <span className="text-[11px] italic text-slate-400">
                    Policy off — alerts use the default threshold.
                  </span>
                ) : (
                  allowlist.map((c) => (
                    <span
                      key={c}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-[10px] font-mono text-emerald-700"
                      data-testid={`allowlist-chip-${c}`}
                    >
                      {flagEmoji(c) || '🏳'} {c}
                    </span>
                  ))
                )}
              </div>
              <button
                type="button"
                onClick={startEditAllowlist}
                className="text-[11px] text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1"
                data-testid="allowlist-edit-btn"
              >
                <Plus className="h-3 w-3" />
                Edit
              </button>
            </>
          ) : (
            <>
              <input
                type="text"
                value={allowlistInput}
                onChange={(e) => setAllowlistInput(e.target.value)}
                placeholder="US, CA, AU"
                className="flex-1 text-[11px] font-mono px-2 py-1 rounded border border-slate-300 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                data-testid="allowlist-input"
                autoFocus
              />
              <button
                type="button"
                onClick={saveAllowlist}
                disabled={allowlistSaving}
                className="text-[11px] text-emerald-700 hover:text-emerald-900 font-medium flex items-center gap-1 disabled:opacity-50"
                data-testid="allowlist-save-btn"
              >
                <Check className="h-3 w-3" /> Save
              </button>
              <button
                type="button"
                onClick={() => setEditingAllowlist(false)}
                className="text-[11px] text-slate-500 hover:text-slate-800 flex items-center gap-1"
                data-testid="allowlist-cancel-btn"
              >
                <X className="h-3 w-3" /> Cancel
              </button>
            </>
          )}
        </div>
        {loading ? (
          <p className="text-xs text-slate-500" data-testid="failed-login-loading">Loading…</p>
        ) : error ? (
          <p className="text-xs text-rose-600" data-testid="failed-login-error">
            Couldn't load failed-login data.
          </p>
        ) : top.length === 0 ? (
          <div className="text-xs text-emerald-700 leading-relaxed flex items-center gap-1.5" data-testid="failed-login-empty">
            <Shield className="h-3.5 w-3.5" />
            No failed-login activity in the last {windowHours === 168 ? '7 days' : `${windowHours} hour${windowHours === 1 ? '' : 's'}`}.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-2 mb-3 text-center">
              <div className="rounded-lg bg-white border border-slate-200 p-2">
                <p className="text-[9px] uppercase tracking-wider text-slate-500">Failures</p>
                <p className="text-lg font-bold text-rose-700 tabular-nums">{totals.events}</p>
              </div>
              <div className="rounded-lg bg-white border border-slate-200 p-2">
                <p className="text-[9px] uppercase tracking-wider text-slate-500">Last hour</p>
                <p className="text-lg font-bold text-rose-700 tabular-nums">{totals.events_last_hour}</p>
              </div>
              <div className="rounded-lg bg-white border border-slate-200 p-2" title="Failed login on a non-existent email — typical credential-stuffing pattern">
                <p className="text-[9px] uppercase tracking-wider text-slate-500">User unknown</p>
                <p className="text-lg font-bold text-rose-700 tabular-nums">{totals.user_not_found}</p>
              </div>
              <div className="rounded-lg bg-white border border-slate-200 p-2" title="Wrong password on a real account — could be a forgetful user OR a targeted attempt">
                <p className="text-[9px] uppercase tracking-wider text-slate-500">Wrong password</p>
                <p className="text-lg font-bold text-rose-700 tabular-nums">{totals.wrong_password}</p>
              </div>
            </div>
            <ol className="divide-y divide-slate-100" data-testid="failed-login-list">
              {top.map((row, i) => (
                <li
                  key={row.ip}
                  className={
                    'flex items-center gap-3 py-2 px-1 ' +
                    (row.unexpected_country ? 'bg-amber-50/60 -mx-1 rounded' : '')
                  }
                  data-testid={`failed-login-row-${i}`}
                >
                  <span className="text-[10px] font-mono text-slate-400 w-5">#{i + 1}</span>
                  <span
                    className="text-sm w-5 flex-shrink-0 text-center"
                    title={row.country_name || 'Unknown country'}
                    data-testid={`failed-login-row-${i}-flag`}
                  >
                    {flagEmoji(row.country_code) || '🏳'}
                  </span>
                  <span className="font-mono text-xs text-slate-800 flex-1 truncate" title={row.ip}>
                    {row.ip}
                  </span>
                  {row.country_code && (
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono w-7 text-center">
                      {row.country_code}
                    </span>
                  )}
                  {row.unexpected_country && (
                    <span
                      className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider text-amber-700 font-bold"
                      title="This IP is from a country outside your allowlist"
                      data-testid={`failed-login-row-${i}-unexpected`}
                    >
                      <AlertTriangle className="h-2.5 w-2.5" />
                      Unexpected
                    </span>
                  )}
                  <HourlySparkline buckets={row.hourly_buckets} ip={row.ip} />
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 tabular-nums w-12 justify-end">
                    <Activity className="h-3 w-3" />
                    {row.count}
                  </span>
                  <span
                    className="inline-flex items-center gap-1 text-[11px] text-slate-500 tabular-nums w-10 justify-end"
                    title={`${row.distinct_emails} distinct email${row.distinct_emails === 1 ? '' : 's'} targeted`}
                  >
                    <Users className="h-3 w-3" />
                    {row.distinct_emails}
                  </span>
                </li>
              ))}
            </ol>
          </>
        )}
      </CardContent>
      )}
    </Card>
  );
}
